import { useState, useEffect } from 'react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Organization_ } from 'src/api/db/apiOrgs'
import { Button } from 'src/shared/components/ui'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Save } from 'lucide-react'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { CompanyProfile } from './components/CompanyProfile'
import { LocalizationRegion } from './components/LocalizationRegion'
import { DangerZone } from './components/DangerZone'

export const GeneralSettings = ({
  organization,
}: {
  organization: Organization_
}) => {
  const { t } = useLocalization()
  const { updateOrganization } = useOrgsApi({})
  const { toast } = useToast()

  // Form state
  const [workspaceName, setWorkspaceName] = useState(organization?.name || '')
  const [timezone, setTimezone] = useState('America/New_York')
  const [currency, setCurrency] = useState(organization?.currency || 'SEK')
  const [language, setLanguage] = useState('sv-SE')
  const [workspaceLogo, setWorkspaceLogo] = useState<string | null>(
    organization?.chosen_logo_url || null
  )

  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    setWorkspaceName(organization?.name || '')
    setWorkspaceLogo(organization?.chosen_logo_url || null)
    setCurrency(organization?.currency || 'SEK')
    setHasChanges(false)
  }, [organization?.name, organization?.chosen_logo_url, organization?.currency])

  const checkForChanges = () => {
    const changed =
      workspaceName !== (organization?.name || '') ||
      workspaceLogo !== (organization?.chosen_logo_url || null) ||
      currency !== (organization?.currency || 'SEK')
    setHasChanges(changed)
  }

  useEffect(() => {
    checkForChanges()
  }, [workspaceName, workspaceLogo, currency, organization])

  const handleSave = async () => {
    if (!organization?.id || !hasChanges) return

    setIsSaving(true)
    try {
      await updateOrganization.mutateAsync({
        id: organization.id,
        name: workspaceName.trim(),
        chosen_logo_url: workspaceLogo,
        currency,
      })
      setHasChanges(false)
      toast({
        variant: 'success',
        title: t('Settings updated', 'Inställningar uppdaterade'),
      })
    } catch (error) {
      console.error('Failed to update settings:', error)
      toast({
        variant: 'destructive',
        title: t(
          'Failed to update settings',
          'Kunde inte uppdatera inställningar'
        ),
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Column className="gap-6">
      <Row className="items-center justify-between">
        <Column className="gap-1">
          <p className="text-sm text-muted-foreground">
            {t(
              'Manage your workspace details, global preferences, and operational defaults.',
              'Hantera dina arbetsyta-detaljer, globala preferenser och operativa standardinställningar.'
            )}
          </p>
        </Column>

        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={isSaving}
            variant="primary"
            isLoading={isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            {t('Save Changes', 'Spara ändringar')}
          </Button>
        )}
      </Row>

      <CompanyProfile
        workspaceName={workspaceName}
        workspaceLogo={workspaceLogo}
        organizationId={organization?.id}
        companyUrl={organization?.company_url ?? undefined}
        onWorkspaceNameChange={setWorkspaceName}
        onWorkspaceLogoChange={setWorkspaceLogo}
      />

      <LocalizationRegion
        timezone={timezone}
        currency={currency}
        language={language}
        onTimezoneChange={setTimezone}
        onCurrencyChange={setCurrency}
        onLanguageChange={setLanguage}
      />

      <DangerZone />
    </Column>
  )
}
